import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { discountAllowsSellingPlan, resolveDiscount } from '@theme/utm-price';
import { canAddToCart } from '@theme/variant-availability';

/**
 * The chosen purchase option, as its radio value. Option blocks are shared across
 * products, so the value still matches after a swap to a sibling.
 * @param {ParentNode} root
 * @returns {string | undefined}
 */
export function getCheckedPurchaseOption(root) {
  const checked = /** @type {HTMLInputElement | null} */ (
    root.querySelector('purchase-option-selector input[type="radio"]:checked')
  );

  return checked?.value || undefined;
}

/**
 * Re-checks a purchase option after a morph replaced it with the incoming product's
 * default. Swapping print keeps the cadence the shopper picked. Does nothing if the
 * sibling doesn't offer it — an option with no matching selling plan renders nothing.
 * @param {ParentNode} root
 * @param {string} value
 */
export function restorePurchaseOption(root, value) {
  const option = /** @type {HTMLInputElement | null} */ (
    root.querySelector(`purchase-option-selector input[type="radio"][value="${CSS.escape(value)}"]`)
  );

  if (!option || option.checked) return;

  option.checked = true;
  option.closest('purchase-option-selector')?.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Wraps the purchase option radios and the nested buy-buttons product form.
 * Card expand/collapse, and whether the quantity selector shows, are handled
 * purely in CSS via `:has()` — this component only copies the checked option's
 * selling plan into the form, since that has to happen in JS to affect what gets
 * submitted to the cart, and answers `getLineItemProperties` for whichever add
 * path is assembling a line.
 *
 * Each radio carries its own `data-selling-plan-id` (empty for a one-time
 * option), so adding cadences needs no change here.
 *
 * The try-for promo price and savings copy are handled declaratively by the
 * nested <utm-price-component> elements (assets/utm-price.js).
 *
 * @extends {Component}
 */
class PurchaseOptionSelector extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.syncSellingPlan();

    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.updateVisibility);
    this.section?.addEventListener('change', this.#handleFastChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.updateVisibility);
    this.section?.removeEventListener('change', this.#handleFastChange);
  }

  /**
   * Mirrors assets/product-oos-form.js's fast path, so this block toggles in lockstep with
   * the OOS form instead of lagging on the fetch-gated `variantUpdate` below.
   * @param {Event} event
   */
  #handleFastChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const picker = target.closest('variant-picker');
    if (!(picker instanceof HTMLElement) || picker.dataset.productId !== this.dataset.productId) return;
    if (picker.querySelectorAll('fieldset').length !== 1) return;

    this.closest('.purchase-options-block')?.toggleAttribute('hidden', target.dataset.optionAvailable === 'false');
  };

  /** A re-render replaces the hidden input with a fresh disabled one. */
  updatedCallback() {
    super.updatedCallback();
    this.syncSellingPlan();
  }

  /**
   * Hides the whole purchase-options block (radios + buy buttons) once the
   * current variant is sold out — no point offering a subscribe/one-time
   * choice for something that can't be added to cart either way.
   * @param {Event} event
   */
  updateVisibility = (event) => {
    const variantUpdateEvent = /** @type {import('@theme/events').VariantUpdateEvent} */ (event);

    if (variantUpdateEvent.detail.data.newProduct) {
      this.dataset.productId = variantUpdateEvent.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    this.closest('.purchase-options-block')?.toggleAttribute('hidden', !canAddToCart(variantUpdateEvent.detail.resource));
  };

  /** @param {Event} _event */
  handleChange(_event) {
    this.syncSellingPlan();
  }

  /**
   * Copies the checked option's selling plan into the form's hidden input.
   *
   * Public because any re-render — a variant change, or the quick view swapping in a
   * sibling product — replaces that input with a fresh disabled one, and morph does
   * not report that to a component it patched in place. product-form.js calls this
   * before reading the form rather than every path having to remember to.
   */
  syncSellingPlan() {
    const checkedOption = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    // Empty for a one-time option, which is what leaves the input out of the POST
    const sellingPlanId = checkedOption?.dataset.sellingPlanId ?? '';

    // All of them, since a merchant can nest more than one buy-buttons block
    for (const input of this.querySelectorAll('input[name="selling_plan"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.disabled = sellingPlanId === '';
      input.value = sellingPlanId;
    }
  }

  /**
   * The price a UTM offer puts this line at, before checkout applies the real code.
   * Gated on the same selling-plan allow-list as the "Try for [price]" copy, and off the same
   * `data-selling-plan` the copy reads, so the two cannot disagree. `discount_value` is the
   * resulting price, in the shop's currency, unconverted.
   *
   * @param {HTMLInputElement | null | undefined} option
   * @returns {string}
   */
  #getUtmLinePrice(option) {
    if (option?.dataset.utmEligible !== 'true') return '';

    const discount = resolveDiscount();
    if (!discountAllowsSellingPlan(discount, option.dataset.sellingPlan)) return '';

    return discount?.discount_value ?? '';
  }

  /**
   * SHINE-563: what the checked option contributes to a cart line — the properties
   * `snippets/cart-products.liquid` reads for a subscription. A one-time option answers
   * the same keys with blanks rather than nothing, which is what clears a stale property
   * left over from a subscription the shopper switched away from. Title/price/frequency
   * come from data attributes rendered on the checked option
   * (blocks/_purchase-option.liquid) rather than parsed from displayed text.
   *
   * Read through `getLineItemProperties` (assets/line-item-properties.js), which is what both
   * add paths assemble a line's properties from — nothing calls this directly.
   *
   * A method rather than a getter: each read mints a fresh `__subscriptionId`, so a
   * caller stamping several lines has to hold one result across all of them.
   *
   * @returns {Record<string, string>}
   */
  getLineItemProperties() {
    const checkedOption = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    const isSubscription = Boolean(checkedOption?.dataset.sellingPlanId);
    const prepaidType = isSubscription ? checkedOption?.dataset.prepaidType ?? '' : '';

    return {
      __isSubscription: isSubscription ? 'true' : '',
      __subscriptionId: isSubscription ? crypto.randomUUID() : '',
      __subscriptionFrequency: isSubscription ? checkedOption?.dataset.subscriptionFrequency ?? '' : '',
      __canChangeQuantity: isSubscription ? 'false' : '',
      // SHINE-563: fires on every subscription add (defaults to "Surprise" when the
      // product has no flavor: tag) — matches util-add-to-cart-data-subscription.js.
      __flavor: isSubscription ? this.dataset.flavor ?? '' : '',
      // Unprefixed — customer-visible at checkout, unlike everything else here.
      'Prepaid Type': prepaidType,
      // Headless pairs an Annually prepaid type with the term's undiscounted total.
      __originalPrice: prepaidType === 'Annually' ? checkedOption?.dataset.originalPrice ?? '' : '',
      __discountedLinePrice: this.#getUtmLinePrice(checkedOption),
    };
  }
}

if (!customElements.get('purchase-option-selector')) {
  customElements.define('purchase-option-selector', PurchaseOptionSelector);
}
